import {
  costSummary,
  inputString,
  totalModelFallbacks,
} from "@tsmono/inspect-common/utils";
import { arrayToString, nullProtoRecord } from "@tsmono/util";

import {
  LogDerived,
  LogHeader,
  SampleDerived,
  SampleSummary,
} from "../api/types";

/**
 * Derivation of stored listing columns (`LogDerived` / `SampleDerived` /
 * the sample facts on `LogHeader`).
 *
 * These run once at ingestion — the write paths (`prepareLogDetails`) attach
 * the result to the stored row, and the listing grids read the stored values
 * without computing. Grid columns must never re-derive these fields at read
 * time; that would let displayed values drift from what the store (and
 * eventually the database query layer) sees.
 */

type LessThan<
  N extends number,
  Acc extends number[] = [],
> = Acc["length"] extends N
  ? Acc[number]
  : LessThan<N, [...Acc, Acc["length"]]>;

/** The valid range for `DERIVE_VERSION`: `DB_VERSION` (schema.ts) packs it
 *  into the two low decimal digits of `SCHEMA_VERSION * 100`, so a value of
 *  100 would silently alias into the next schema version's namespace. */
type DeriveVersion = LessThan<100>;

/**
 * Version of the derivation behavior in this module, folded into the
 * database version (see `DB_VERSION` in schema.ts). Bump it on ANY behavior
 * change here: persisted rows carry values computed by the old logic and are
 * only recomputed via the recreate-on-mismatch wipe. Must stay below 100
 * (enforced by `DeriveVersion`; schema.ts asserts it again at runtime).
 */
export const DERIVE_VERSION: number = 3 satisfies DeriveVersion;

export const deriveLogFields = (header: LogHeader): LogDerived => {
  let total_tokens: number | undefined;
  if (header.stats?.model_usage) {
    total_tokens = 0;
    for (const usage of Object.values(header.stats.model_usage)) {
      total_tokens += usage.total_tokens;
    }
  }

  let duration: number | undefined;
  if (header.stats?.started_at && header.stats.completed_at) {
    const start = new Date(header.stats.started_at).getTime();
    const end = new Date(header.stats.completed_at).getTime();
    if (start && end && end > start) {
      duration = (end - start) / 1000;
    }
  }

  // `task_args_passed` (the args the user actually supplied at the call
  // site, filled by normalizeEvalSpec) rather than `task_args` (which would
  // also include defaulted values).
  let task_args: string | undefined;
  const entries = Object.entries(header.eval.task_args_passed);
  if (entries.length > 0) {
    task_args = entries.map(([k, v]) => `${k}=${JSON.stringify(v)}`).join(", ");
  }

  let percent_completed: number | undefined;
  const total = header.results?.total_samples;
  const completed = header.results?.completed_samples;
  if (total && total > 0 && completed !== undefined) {
    percent_completed = (completed / total) * 100;
  }

  const sample_limits =
    header.sampleLimits.length > 0 ? header.sampleLimits.join(", ") : undefined;

  // Key by (scorer, metric) so distinct scorers emitting the same metric name
  // each get their own entry. Reducer is omitted: `reducer=null` (default,
  // silently mean) and `reducer="mean"` (explicit) land in the same slot
  // since the underlying computation is identical.
  const byScorer = new Map<string, Map<string, number>>();
  for (const evalScore of header.results?.scores ?? []) {
    for (const [metricName, metric] of Object.entries(evalScore.metrics)) {
      let metrics = byScorer.get(evalScore.name);
      if (!metrics) {
        metrics = new Map();
        byScorer.set(evalScore.name, metrics);
      }
      metrics.set(metricName, metric.value);
    }
  }
  const scores =
    byScorer.size > 0
      ? nullProtoRecord(
          new Map(
            Array.from(byScorer, ([name, metrics]) => [
              name,
              nullProtoRecord(metrics),
            ])
          )
        )
      : undefined;

  return {
    total_tokens,
    duration,
    task_args,
    percent_completed,
    sample_limits,
    scores,
  };
};

/** The sample facts baked onto the stored `LogHeader` by `toLogHeader` —
 *  persisted derivation like the fields above, so it lives under
 *  `DERIVE_VERSION` with them. */
export const deriveSampleFacts = (
  summaries: SampleSummary[]
): Pick<LogHeader, "sampleCount" | "sampleErrorCount" | "sampleLimits"> => {
  const limits = new Set<string>();
  let errorCount = 0;
  for (const sample of summaries) {
    if (sample.error) errorCount += 1;
    if (sample.limit) limits.add(sample.limit);
  }
  return {
    sampleCount: summaries.length,
    sampleErrorCount: errorCount,
    sampleLimits: [...limits].sort(),
  };
};

/** A sample's total token spend across models (undefined before any usage).
 *  The single home for this sum — the stored `derived.tokens` column and
 *  read-time consumers (filter variables, sample rows) must agree. */
export const totalSampleTokens = (
  modelUsage: SampleSummary["model_usage"]
): number | undefined => {
  const usages = Object.values(modelUsage);
  return usages.length > 0
    ? usages.reduce((sum, u) => sum + u.total_tokens, 0)
    : undefined;
};

/** A sample's total cost across models — undefined unless every model that
 *  used tokens was priced, so a partial sum never reads as the full cost. */
export const totalSampleCost = (
  modelUsage: SampleSummary["model_usage"]
): number | undefined => {
  const cost = costSummary(modelUsage);
  return cost && !cost.partial ? cost.total : undefined;
};

export const deriveSampleFields = (summary: SampleSummary): SampleDerived => {
  const scores = summary.scores
    ? nullProtoRecord(
        new Map(
          Object.entries(summary.scores).map(([name, score]) => [
            name,
            score.value,
          ])
        )
      )
    : undefined;

  return {
    tokens: totalSampleTokens(summary.model_usage),
    cost: totalSampleCost(summary.model_usage),
    input: inputString(summary.input).join("\n"),
    target: arrayToString(summary.target),
    // 0 fallbacks stores as undefined so the column renders empty
    fallbacks: totalModelFallbacks(summary.model_fallbacks) || undefined,
    scores,
  };
};
